import type { Tool } from './types';

export const freeKundli: Tool = {
  slug: 'free-kundli',
  metaTitle: 'Free Kundli Online by Date of Birth — AI Janam Kundali',
  metaDescription:
    'Generate your free Janam Kundali (Vedic birth chart) online by date, time and place of birth, then ask an AI Vedic astrologer about your chart — instant and free.',
  keywords:
    'free kundli, free kundli online, janam kundali, kundli by date of birth, ai kundli, free birth chart, vedic birth chart',
  eyebrow: 'Free Birth Chart',
  h1: 'Free Kundli Online by Date of Birth',
  appName: 'KundaliAI Free Kundli',
  calculator: 'free-kundli',
  intro:
    'Enter your date, time and place of birth and KundaliAI builds your complete Janam Kundali (Vedic birth chart) in seconds — planetary positions, Lagna, Rashi, Nakshatra and the major Doshas — then lets you ask an AI Vedic astrologer anything about it. No sign-up needed to start.',
  ctaLabel: 'Generate my free Kundli',
  ctaHref: '/chat',
  sections: [
    {
      heading: 'What is a Janam Kundali?',
      body: 'A Janam Kundali — also called a birth chart or horoscope — is a snapshot of the sky at the exact moment you were born. It maps the twelve houses (bhavas), the positions of the nine planets (grahas), your ascendant (Lagna), Moon sign (Rashi) and birth star (Nakshatra). Vedic astrologers read these placements to understand personality, career, relationships, health and the timing of life events through Dasha periods.',
    },
    {
      heading: 'How KundaliAI generates your chart',
      body: "We calculate planetary longitudes from precise astronomical ephemeris data for your birth moment, then apply classical Jyotish rules to produce your chart. Unlike a static PDF report, KundaliAI turns your Kundali into a **conversation**: ask *'which planet rules my career?'* or *'when does my Mahadasha change?'* and the AI answers grounded in your own placements, in plain language.",
    },
    {
      heading: 'What you need for an accurate Kundli',
      body: 'For the most accurate reading, provide three things:\n\n- **Date of birth** — day, month and year.\n- **Time of birth** — as exact as possible (from a birth certificate or hospital record ideally), since the Lagna changes roughly every two hours.\n- **Place of birth** — city and country, used to fix latitude, longitude and time zone.\n\nIf you don\'t know your exact birth time, you can still generate a Moon-chart based reading.',
    },
    {
      heading: 'Free to start, no account required',
      body: 'Generating your Kundali and asking your first questions is completely free — no credit card, no account needed to begin. Create a free account to save charts, generate charts for family, and unlock detailed predictions, compatibility and remedy reports.',
    },
  ],
  faq: [
    {
      question: 'Is the Kundli really free?',
      answer:
        'Yes. You can generate your Janam Kundali and ask an AI Vedic astrologer your first questions for free, without signing up. Premium plans add saved charts, detailed predictions and full reports.',
    },
    {
      question: 'What if I don’t know my exact birth time?',
      answer:
        'You can still get a meaningful reading based on your Moon sign (Rashi) and Nakshatra. An exact birth time is only needed to fix the Lagna (ascendant) precisely, which affects house-based predictions.',
    },
    {
      question: 'How accurate is an AI-generated Kundli?',
      answer:
        'The chart itself is computed from precise astronomical positions, so the planetary placements are as accurate as any professional software. The interpretation applies classical Vedic astrology rules, delivered conversationally by AI.',
    },
    {
      question: 'Is my birth data private?',
      answer:
        'Your birth details are used only to compute your chart and generate your reading. See our privacy policy for how data is handled.',
    },
  ],
  related: [
    { label: 'Talk to an AI astrologer', href: '/ai-astrologer' },
    { label: 'Kundli matching by AI', href: '/kundli-matching' },
    { label: 'Mangal Dosha calculator', href: '/mangal-dosha-calculator' },
    { label: 'Learn Vedic astrology concepts', href: '/astrology' },
  ],
};
